// src/app/services/menu.service.ts
import { Injectable } from '@angular/core';
import { MenuItem } from '../shared/components/menu/menu.component';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class MenuService {
  // Definición centralizada de todos los ítems de menú posibles
  private readonly allMenuItems: Record<string, MenuItem> = {
    // Sección de Dashboard
    dashboard: {
      title: 'Tablero',
      url: '/welcome',
      icon: 'grid',
    },

    settings: {
      title: 'Configuración',
      url: '/settings',
      icon: 'settings',
    },

    patients: {
      title: 'Pacientes',
      icon: 'people',
      url: '/pacientes',
    },

    pacientes_proximos: {
      title: 'Pacientes Próximos',
      icon: 'people',
      url: '/expediente/proximos',
    },

    // Sección de Pacientes
    patients2: {
      title: 'Pacientes',
      icon: 'people',
      children: [
        {
          id: 'patients-search',
          title: 'Buscar Paciente',
          url: '/expedientes/medico',
          icon: 'search',
        },
        {
          id: 'patients-new',
          title: 'Nuevo Paciente',
          url: '/expedientes/nuevo',
          icon: 'person-add',
        },
      ],
    },

    // Sección de Agenda
    agenda: {
      title: 'Agenda',
      icon: 'calendar',
      children: [
        {
          id: 'agenda-schedule',
          title: 'Programación de Citas',
          url: '/agenda',
          icon: 'calendar-number',
        },
      ],
    },

    rentas: {
      title: 'Rentas',
      icon: 'swap-horizontal',
      children: [
        {
          id: 'calendario',
          title: 'Programación de Rentas',
          url: '/calendario-rentas',
          icon: 'calendar-number',
        },
        {
          id: 'reportes',
          title: 'Listado de Reportes',
          url: '/reportes',
          icon: 'document-text',
        },
        {
          id: 'equipos',
          title: 'Equipos',
          url: '/equipos',
          icon: 'cube',
        },
        {
          id: 'catalogos',
          title: 'Catalogos',
          url: '/catalogos-rentas',
          icon: 'list',
        },
      ],
    },

    rentas_calendario: {
      id: 'rent-schedule',
      title: 'Programación de Rentas',
      url: '/calendario-rentas',
      icon: 'calendar-number',
    },
    rentas_reportes: {
      id: 'rent-schedule',
      title: 'Listado de Reportes',
      url: '/reportes',
      icon: 'document-text',
    },
    rentas_equipos: {
      id: 'rent-schedule',
      title: 'Equipos',
      url: '/equipos',
      icon: 'cube',
    },
    rentas_catalogs: {
      id: 'rent-catalogs',
      title: 'Catalogos',
      url: '/catalogos-rentas',
      icon: 'list',
    },

    // Sección de Agenda
    staff: {
      title: 'Usuarios',
      icon: 'people-circle',
      url: '/staff',
    },

    // Sección de Censo Hospitalario
    census: {
      title: 'Censo Hospitalario',
      url: '/hospitalizacion/censo',
      icon: 'business',
    },

    // Sección de Administración
    admin: {
      title: 'Administración',
      icon: 'settings',
      children: [
        {
          id: 'admin-users',
          title: 'Usuarios',
          url: '/admin/usuarios',
          icon: 'people',
        },
        {
          id: 'admin-roles',
          title: 'Roles y Permisos',
          url: '/admin/roles',
          icon: 'key',
        },
        {
          id: 'admin-catalogs',
          title: 'Catálogos',
          url: '/admin/catalogos',
          icon: 'list',
        },
        {
          id: 'admin-settings',
          title: 'Configuración',
          url: '/admin/configuracion',
          icon: 'cog',
        },
      ],
    },

    // Sección de Equipamiento
    equipment: {
      title: 'Equipos',
      icon: 'videocam',
      children: [
        {
          id: 'equipment-inventory',
          title: 'Inventario',
          url: '/equipamiento/inventario',
          icon: 'add-circle',
        },
        {
          id: 'equipment-rental',
          title: 'Rentas y Mantenimientos',
          url: '/equipamiento/rentas',
          icon: 'calendar',
        },
      ],
    },

    // Directorio médico
    directory: {
      title: 'Directorio médico',
      icon: 'list-circle',
      url: '/directorio',
    },

    // Sección de Finanzas
    finance: {
      title: 'Finanzas',
      icon: 'cash',
      children: [
        {
          title: 'Remisiones',
          url: '/remisiones',
          icon: 'receipt',
        },
        {
          id: 'finance-company',
          title: 'Empresas',
          url: '/empresas',
          icon: 'business',
        },
        {
          id: 'finance-collection',
          title: 'Servicios e Insumos',
          url: 'servicios',
          icon: 'card',
        },
        // {
        //   id: 'finance-insurance',
        //   title: 'Seguros',
        //   url: '/finanzas/seguros',
        //   icon: 'shield',
        // },
        // {
        //   id: 'finance-reports',
        //   title: 'Reportes',
        //   url: '/finanzas/reportes',
        //   icon: 'stats-chart',
        // },
      ],
    },

    // Sección de Comunicación
    communication: {
      title: 'Comunicación',
      icon: 'chatbubble',
      children: [
        {
          id: 'comm-notifications',
          title: 'Notificaciones',
          url: '/comunicacion/notificaciones',
          icon: 'notifications',
        },
        {
          id: 'comm-messages',
          title: 'Recados y Pendientes',
          url: '/comunicacion/recados',
          icon: 'checkbox',
        },
      ],
    },

    // Sección de Facturación y Cobros (vista simplificada)
    billing: {
      title: 'Facturación y Cobros',
      url: '/patient-billing',
      icon: 'cash',
    },

    // Sección de Estudios
    studies: {
      title: 'Estudios',
      icon: 'flask',
      children: [
        {
          id: 'studies-requests',
          title: 'Solicitudes',
          url: '/estudios/solicitudes',
          icon: 'search',
        },
        {
          id: 'studies-results',
          title: 'Resultados',
          url: '/estudios/resultados',
          icon: 'document',
        },
        {
          id: 'studies-imaging',
          title: 'Imagenología',
          url: '/estudios/imagenologia',
          icon: 'image',
        },
      ],
    },

    // Sección de Recetas
    prescriptions: {
      title: 'Recetas',
      icon: 'medical',
      children: [
        {
          id: 'prescriptions-new',
          title: 'Nueva Receta',
          url: '/recetas/nueva',
          icon: 'add-circle',
        },
        {
          id: 'prescriptions-history',
          title: 'Buscar',
          url: '/recetas/historial',
          icon: 'search',
        },
      ],
    },

    // Sección de Almacén
    warehouse: {
      title: 'Almacén',
      icon: 'cube',
      children: [
        {
          id: 'warehouse-equipment',
          title: 'Equipos',
          url: '/equipamiento/inventario',
          icon: 'videocam',
        },
      ],
    },

    // Sección de Recados y Pendientes
    messages: {
      title: 'Recados y Pendientes',
      icon: 'chatbubble',
      url: '/recados',
    },

    // Sección Remisiones
    remissions: {
      title: 'Remisiones',
      url: '/remisiones',
      icon: 'receipt',
    },

    // Secciones específicas de Nutrición
    nutrition: {
      title: 'Historia Clínica',
      url: '/nutricion',
      icon: 'document-text',
    },

    anthropometrics: {
      title: 'Antropométricos',
      url: '/nutricion-antropometricos',
      icon: 'analytics',
    },

    biochemicals: {
      title: 'Bioquímicos',
      url: '/nutricion-bioquimicos',
      icon: 'flask',
    },

    nutritional_tx: {
      title: 'Tx Nutricional',
      url: '/nutricion-tx',
      icon: 'nutrition',
    },

    screenings: {
      title: 'Tamizajes',
      url: '/nutricion-tamizajes',
      icon: 'clipboard',
    },

    parenteral: {
      title: 'Parenteral',
      url: '/nutricion-parenteral',
      icon: 'beaker',
    },

    enteral: {
      title: 'Enteral',
      url: '/nutricion-enteral',
      icon: 'flask',
    },

    recipes: {
      title: 'Recetas y Menús',
      url: '/recetas',
      icon: 'restaurant',
    },

    // Sección Nutrición agrupada
    nutrition_group: {
      title: 'Nutrición',
      icon: 'nutrition',
      children: [
        {
          id: 'nutrition',
          title: 'Historia Clínica',
          url: '/nutricion',
          icon: 'document-text',
        },
        {
          id: 'anthropometrics',
          title: 'Antropométricos',
          url: '/nutricion-antropometricos',
          icon: 'analytics',
        },
        ...(environment.production
          ? []
          : [
              {
                id: 'biochemicals',
                title: 'Bioquímicos',
                url: '/nutricion-bioquimicos',
                icon: 'flask',
              },
            ]), // Solo en desarrollo
        {
          id: 'nutritional_tx',
          title: 'Tx Nutricional',
          url: '/nutricion-tx',
          icon: 'nutrition',
        },
        {
          id: 'screenings',
          title: 'Tamizajes',
          url: '/nutricion-tamizajes',
          icon: 'clipboard',
        },
        {
          id: 'parenteral',
          title: 'Parenteral',
          url: '/nutricion-parenteral',
          icon: 'beaker',
        },
        {
          id: 'enteral',
          title: 'Enteral',
          url: '/nutricion-enteral',
          icon: 'flask',
        },
        {
          id: 'recipes',
          title: 'Recetas y Menús',
          url: '/recetas',
          icon: 'restaurant',
        },
      ],
    },
  };

  // Mapeo de roles a menús
  private readonly roleMenuMappings: Record<string, string[]> = {
    technician: ['rentas_calendario', 'rentas_reportes', 'rentas_equipos', 'rentas_catalogs'],

    admin: [
      // 'dashboard',
      'agenda',
      'rentas',
      'patients',
      'nutrition_group',
      // 'finance',
      // ...(environment.production ? [] : ['finance']), // Solo en desarrollo
      'staff',
      'settings',
      'pacientes_proximos',
      // 'census',
      // 'admin',
      // 'equipment',
      // 'directory',
      // 'communication',
    ],

    receptionist: ['agenda', 'patients'],

    doctor: ['agenda'],

    nutritionist: [
      'dashboard',
      'nutrition',
      'anthropometrics',
      // 'biochemicals',
      'nutritional_tx',
      'screenings',
      'parenteral',
      'enteral',
      'recipes',
      'messages',
    ],

    financial: ['finance'],
    accountant: ['finance'],
  };

  /**
   * Obtiene un ítem de menú por su ID
   * @param id Identificador del ítem de menú
   * @returns El ítem de menú o null si no existe
   */
  private getMenuItemById(id: string): MenuItem | null {
    // Si el ID contiene un punto, es un elemento hijo
    if (id.includes('.')) {
      const [parentId, childId] = id.split('.');
      const parent = this.allMenuItems[parentId];

      if (parent && parent.children) {
        const child = parent.children.find((item) => item.id === childId);
        return child || null;
      }
      return null;
    }

    // Si no tiene punto, es un elemento padre
    return this.allMenuItems[id] || null;
  }

  /**
   * Construye la estructura de menú para un rol específico
   * @param menuIds IDs de los elementos de menú a incluir
   * @returns Estructura de menú filtrada
   */
  private buildMenuFromIds(menuIds: string[]): MenuItem[] {
    const menu: MenuItem[] = [];
    const processedParents = new Set<string>();

    for (const id of menuIds) {
      // Para elementos hijo (formato: padre.hijo)
      if (id.includes('.')) {
        const [parentId, childId] = id.split('.');
        const parentItem = this.allMenuItems[parentId];

        if (parentItem && parentItem.children) {
          const childItem = parentItem.children.find((item) => item.id === childId);

          // Si el elemento hijo existe
          if (childItem) {
            // Si ya hemos procesado el padre, lo buscamos en el menú
            if (processedParents.has(parentId)) {
              const existingParent = menu.find(
                (item) => item.title === parentItem.title && item.icon === parentItem.icon,
              );

              if (existingParent && existingParent.children) {
                existingParent.children.push(childItem);
              }
            }
            // Si no hemos procesado el padre, creamos una copia nueva
            else {
              const newParent: MenuItem = {
                ...parentItem,
                children: [childItem],
              };
              menu.push(newParent);
              processedParents.add(parentId);
            }
          }
        }
      }
      // Para elementos padre
      else {
        const item = this.allMenuItems[id];
        if (item) {
          menu.push({ ...item });
          if (item.children) {
            processedParents.add(id);
          }
        }
      }
    }

    return menu;
  }

  /**
   * Método público para obtener el menú basado en el rol
   * @param role Rol del usuario
   * @returns Estructura de menú para el rol especificado
   */
  getMenuByRole(role: string): MenuItem[] {
    const normalizedRole = role?.toLowerCase().trim();

    // Obtener los IDs de menú para el rol
    const menuIds = this.roleMenuMappings[normalizedRole] || this.roleMenuMappings['doctor'];

    // Construir el menú basado en los IDs
    return this.buildMenuFromIds(menuIds);
  }

  /**
   * Construye un menú personalizado basado en permisos específicos
   * @param permissions Lista de permisos del usuario
   * @returns Estructura de menú personalizada
   */
  getMenuByPermissions(permissions: string[]): MenuItem[] {
    // Este método podría implementarse en el futuro para crear menús
    // dinámicamente basados en permisos individuales en lugar de roles

    // Por ahora, usamos un mapeo predefinido y devolvemos el menú predeterminado
    return this.getMenuByRole('doctor');
  }
}
